import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";

import { useDeletePaymentMethod } from "api/payments/index.js";

/**
 * RemovePaymentMethod - Component to conditionally display a modal that allows the ability to remove a payment method.
 * @param {RemovePaymentMethodProps} props
 * @param {Boolean} props.isOpen - Dictates whether modal is open or closed.
 * @param {Function} props.onClose - function to close modal.
 * @param {Object} props.paymentMethod - The payment method that this component is responsible for.
 * @param {Function} props.onComplete - callback function to handle success case.
 * @returns JSX.Element
 */
const RemovePaymentMethodModal = ({ isOpen, onClose, paymentMethod, onComplete }) => {
  const { formatMessage } = useIntl();
  const {
    mutateAsync: deletePaymentMethod,
    isSuccess: isDeletingPMSuccess,
    isLoading: isDeletingPM,
    error: deletePMError,
  } = useDeletePaymentMethod();

  const toast = useToast();

  const handleSubmit = async () => {
    try {
      await deletePaymentMethod({ id: paymentMethod.id });
    } catch {
      // NO-OP as 'deletePMError' gets handled in a useEffect below
    }
  };

  useEffect(() => {
    if (isDeletingPMSuccess) {
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Payment method successfully removed",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      onComplete();
      onClose();
    }
  }, [isDeletingPMSuccess, onComplete, toast, onClose, formatMessage]);

  useEffect(() => {
    if (deletePMError) {
      const pmHasActivePayoutsErrorMsg = formatMessage({
        defaultMessage: "This payment method is currently tied to an in-progress payout and can not be deleted.",
        description: "Error message to tell the user we can not delete a payment method that has in-progress payouts tied to it.",
      });
      const errCodes = deletePMError.data?.codes || [];
      const desc = errCodes.includes("PAYMENT_METHOD_ACTIVE_DELETE_ERROR") ? pmHasActivePayoutsErrorMsg : deletePMError.message;
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description: desc,
        status: "error",
        isClosable: true,
      });
    }
  }, [deletePMError, toast, formatMessage]);
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <FormattedMessage defaultMessage="Remove Payment Method" description="Modal header" />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormattedMessage
            defaultMessage="Are you sure you want to remove this payment method?"
            description="Modal body text"
          />
        </ModalBody>
        <ModalFooter justifyContent="flex-end">
          <Button onClick={handleSubmit} isDisabled={isDeletingPM}>
            <FormattedMessage defaultMessage="Submit" description="Button label" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RemovePaymentMethodModal;
